/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates an engine selector
 * required params:
 *  - data
 *  Example : 
 *  [
        "engine1",
        "engine2",
        "engine3"
    ];
     - checkedEngines : array returned from useState 
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Checkbox from "./Checkbox";
import Table from "./Table";
import Input from "./Input";
import Spinner from "./Spinner";

function TableAdvanced({
    sort,
    data,
    search = false,
    checkbox = false,
    disabled,
    backgroundColor = "#121212",
    headerColor,
    hoverRowColor,
    textColor = "#c6c6c6",
    setChecked,
    checkboxesState,
    onRowClick,
    width = 16,
    height = 10,
    tableRows,
    textSizeDecimal,
    keyboardHandler,
    disabledList = false,
    aframeClasses,
    micStates,
    inVRMode,
    ...otherAttributes
}) {
    if (data.length < 1) throw new Error("No data");
    useEffect(() => {
        if (!disabledList)
            disabledList = data.map(() => {
                return true;
            });
        if (search)
            return () => {
                keyboardHandler("us", false, null);
            };
    }, [search, keyboardHandler]);

    const [searchText, setSearchText] = useState("");
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [order, setOrder] = useState(
        new Array(data[0].length + (checkbox ? 1 : 0)).fill(false)
    );

    const sortFunction = (column, currentOrder) => {
        const newOrder =
            !currentOrder || currentOrder === "down" ? "up" : "down";
        if (checkbox) column--;
        setOrder((old) => {
            return old.map((x, i) =>
                i === column + (checkbox ? 1 : 0) ? newOrder : false
            );
        });
    };

    // Create and places checkboxes from array
    const prepareCheckbox = (data) => {
        return data.map((elem, i) => {
            const id = elem[0];
            return () => {
                switch (disabledList[i]) {
                    case "checking":
                        return (
                            <Spinner
                                radius={inVRMode ? 0.05 : 0.4}
                                position={`0 0 ${width / 100}`}
                            />
                        );
                    case "not available":
                        return (
                            <a-image
                                width={height / 12}
                                height={height / 12}
                                position={`0 0 ${width / 100}`}
                                src={"#Warning"}
                                opacity={disabled ? "0.5" : "1"}
                            />
                        );
                    case "available":
                    default:
                        const elements = data.map((d) => d[0]);
                        const checkingElements = disabledList.filter(
                            (el) => el === "checking"
                        );
                        const unavailableElements = disabledList.filter(
                            (el) => el === "not available"
                        );

                        return (
                            <Checkbox
                                position={`0 0 ${width / 100}`}
                                id={id}
                                aframeClasses={aframeClasses}
                                checked={
                                    elements.length - 1 ===
                                        unavailableElements.length +
                                            checkingElements.length +
                                            checkboxesState.length ||
                                    checkboxesState.includes(id)
                                }
                                side={height * 0.08}
                                textColor={textColor}
                                uncheckedColor={backgroundColor}
                                setChecked={setChecked}
                                backgroundColor={"#262626"}
                                disabled={disabled}
                            />
                        );
                }
            };
        });
    };

    const searchFunc = (dataToSearch) => {
        return dataToSearch.filter((x) => {
            return (
                x[0] &&
                x[0].toString().toLowerCase().includes(searchText.toLowerCase())
            );
        });
    };

    let tableData = [data[0], ...searchFunc(data.slice(1))];

    if (checkbox) {
        const checkboxes = prepareCheckbox(tableData);
        tableData = tableData.map((e, i) => [checkboxes[i], ...e]);
    }
    if (tableRows) {
        tableData = tableData.map((x, id) => x.slice(0, tableRows + 1));
    }
    if (order.includes("up") || order.includes("down")) {
        const column = order.reduce(
            (p, c, id) => (["up", "down"].includes(c) ? id : p),
            0
        );
        const sortedData =
            order[column] === "up"
                ? tableData.slice(1).sort((a, b) => {
                      return a[column].localeCompare(b[column]);
                  })
                : tableData
                      .slice(1)
                      .sort((a, b) => {
                          return a[column].localeCompare(b[column]);
                      })
                      .reverse();
        tableData = [tableData[0], ...sortedData];
    }

    return (
        <a-entity class="TableAdvanced" {...otherAttributes}>
            {search && (
                <Input
                    position={`0 0 ${width / 500}`}
                    width={width}
                    height={height / 10}
                    backgroundColor="#000"
                    text={searchText}
                    placeholder={"Search"}
                    aframeClasses={aframeClasses}
                    textSizeDecimal={textSizeDecimal || 20 / height}
                    onClickHandler={() => {
                        keyboardHandler("us", true, (callback) => {
                            setSearchText((old) => callback(old));
                        });

                        setIsInputFocused(true);
                    }}
                    focused={isInputFocused}
                />
            )}
            <Table
                micStates={micStates}
                position={`0 ${-height / 20 - (height / 20) * 9} 0`}
                width={width}
                height={(height / 10) * 9}
                sort={sort}
                isSortingUp={order}
                sortFunction={sortFunction}
                aframeClasses={aframeClasses}
                data={tableData}
                disabledList={disabledList}
                inVRMode={inVRMode}
                onRowClick={
                    onRowClick
                        ? (filteredPosition, row) =>
                              onRowClick(
                                  data.indexOf(row) - 1,
                                  filteredPosition
                              )
                        : onRowClick
                }
                textSizeDecimal={textSizeDecimal}
                isReloadingEnabled={search}
                backgroundColor={backgroundColor}
                headerColor={headerColor}
                hoverRowColor={hoverRowColor}
            />
        </a-entity>
    );
}

TableAdvanced.propTypes = {
    data: PropTypes.array.isRequired
};

export default TableAdvanced;
